// client/src/coms/workspace/tabMemory.js
// Per-conversation view state that should survive switching tabs within this page: scroll position (as a message
// anchor), the pending reply/quote and the message search. Kept in memory only, keyed by account, so nothing
// private is written to shared storage and switching account (which reloads the app) forgets it. Drafts are
// persisted separately by the composers, also per account.
const store = new Map();
const MAX_ENTRIES = 60;

function key(userId, convKey) {
  return `${String(userId || '')}|${String(convKey || '')}`;
}

export function readTabMemory(userId, convKey) {
  if (!convKey) return null;
  return store.get(key(userId, convKey)) || null;
}

export function writeTabMemory(userId, convKey, patch) {
  if (!convKey) return;
  const k = key(userId, convKey);
  const next = { ...(store.get(k) || {}), ...patch, at: Date.now() };
  store.delete(k);
  store.set(k, next);
  while (store.size > MAX_ENTRIES) store.delete(store.keys().next().value);
}

export function clearTabMemory() {
  store.clear();
}

// Records where a scrolled message list is: the first message whose bottom is below the top edge, and its
// offset, or "at bottom" (the list then reopens at the newest message as usual).
export function captureScroll(scroller) {
  if (!scroller) return null;
  const remaining = scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight;
  if (remaining <= 80) return { atBottom: true };
  const top = scroller.getBoundingClientRect().top;
  const rows = scroller.querySelectorAll('[data-message-id]');
  for (const row of rows) {
    const r = row.getBoundingClientRect();
    if (r.bottom > top + 1) {
      return { atBottom: false, anchorId: row.getAttribute('data-message-id'), anchorOffset: r.top - top, scrollTop: scroller.scrollTop };
    }
  }
  return { atBottom: false, scrollTop: scroller.scrollTop };
}

// Restores a captured position inside this scroller only (never document-wide: split panes have their own).
export function restoreScroll(scroller, saved) {
  if (!scroller || !saved || saved.atBottom) return false;
  if (saved.anchorId) {
    let row = null;
    try {
      row = scroller.querySelector(`[data-message-id="${typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(saved.anchorId) : saved.anchorId}"]`);
    } catch {}
    if (row) {
      const top = scroller.getBoundingClientRect().top;
      scroller.scrollTop += row.getBoundingClientRect().top - top - (Number(saved.anchorOffset) || 0);
      return true;
    }
  }
  if (Number.isFinite(saved.scrollTop) && scroller.scrollHeight > scroller.clientHeight) {
    scroller.scrollTop = saved.scrollTop;
    return true;
  }
  return false;
}
